import React, { Component, Fragment } from 'react';
import Typography from '../../component/Typography';
import withStyles from '@mui/styles/withStyles';
import { getOfficeFromKey } from '../../util/brands';
import { AltButton } from '../../component/form/PrimaryButton';
import Grid from '../../component/form/Grid';
import Paper from '@mui/material/Paper';
import Table, {
    TableCell,
    TableHeaderCell,
    TableHeaderRow,
    TableRow,
    TableSecondaryHeaderRow
} from '../../component/form/Table';
import { Query } from '../../reactApolloCompat';
import Spinner from '../../component/Spinner';
import PrintContent from '../../component/PrintContent';
import { flattenConnection } from '../../util/functions';
import { getProperty, isNullOrUndefined } from '../../util/objects';
import cloneDeep from 'lodash.clonedeep';
import { formStyles } from '../mortuary/Common/FormFields';
import moment from 'moment';
import { renderRunSheetDetails } from './DailyRunSheetConstants';
import { joinDefined, prettyPhone, stringIsNullOrEmpty } from '../../util/strings';
import { LinearProgress } from '@mui/material';
import { PrintIcon } from '../../component/IconIndex';
import { InlineField } from '../../component/form/Inline';
import LinkButton from '../../component/form/LinkButton';
import { withSnackbarMessage } from '../../context/SnackbarMessage';
import { compose } from '../../reactApolloCompat';
import { DailyRunSheetFragment } from './DailyRunSheetFragment2';
import { isRelatedObjectUndefined } from '../../util/graphql';
import { isContactDefined } from '../../util/bookable';
import Hidden from '../../component/Hidden';
import DatePickerField from '../../component/form/DatePickerField';
import { GQL_REFETCH_60S, subscribe } from '../../util/subscriptions';
import Separator from '../../component/form/Separator';
import { getOffices } from '../../environment';
import cx from 'classnames';
import { withErrorBoundary } from '@sentry/react';
import ErrorFallback from '../../component/ErrorFallback';

class DailyRunSheet extends Component {
    constructor(props) {
        super(props);

        const saved = JSON.parse(sessionStorage.getItem('runsheet')) || {};
        const rawDate = props.match.params.date || saved.date || moment().format('YYYYMMDD');

        const date = this.validDate(rawDate) ? moment(rawDate) : moment();

        this.state = {
            dateFrom: date.format('YYYY-MM-DD'),
            dateTo: date.format('YYYY-MM-DD'),
            renderDateFrom: date.format('YYYY-MM-DD'),
            renderDateTo: date.format('YYYY-MM-DD')
        };

        this.formRef = React.createRef(); // printing

        this.runsheetQueryRef = React.createRef();
        subscribe(this.runsheetQueryRef, GQL_REFETCH_60S);
    }

    // only allow run sheet to use dates from 21st century and up to two years in the future.
    validDate = rawDate => {
        const date = moment(rawDate);
        return !(!date.isValid() || date.isBefore('2001-01-01') || date.isAfter(moment().add(2, 'year')));
    };

    dateChange = e => {
        const { dateFrom, dateTo } = this.state;
        const diff = moment(dateTo).diff(dateFrom, 'days');
        // enforce a maximum of 7 days in the runsheet.
        const newDates = { dateFrom, dateTo };

        if (e.target.name === 'dateFrom') {
            if (moment(e.target.value).isAfter(dateTo)) {
                newDates.dateFrom = moment(e.target.value).format('YYYY-MM-DD');
                newDates.dateTo = moment(e.target.value)
                    .add(diff, 'days')
                    .format('YYYY-MM-DD');
            } else if (moment(e.target.value).isBefore(moment(dateTo).subtract(7, 'days'))) {
                newDates.dateFrom = moment(e.target.value).format('YYYY-MM-DD');
                newDates.dateTo = moment(e.target.value)
                    .add(diff, 'days')
                    .format('YYYY-MM-DD');
            } else if (!!e.target.value) {
                newDates.dateFrom = e.target.value;
            }
        } else {
            if (moment(e.target.value).isBefore(dateFrom)) {
                newDates.dateTo = moment(e.target.value).format('YYYY-MM-DD');
                newDates.dateFrom = moment(e.target.value)
                    .subtract(diff, 'days')
                    .format('YYYY-MM-DD');
            } else if (moment(e.target.value).isAfter(moment(dateFrom).add(7, 'days'))) {
                newDates.dateTo = moment(e.target.value).format('YYYY-MM-DD');
                newDates.dateFrom = moment(e.target.value)
                    .subtract(diff, 'days')
                    .format('YYYY-MM-DD');
            } else if (!!e.target.value) {
                newDates.dateTo = e.target.value;
            }
        }

        // only update run sheet if sensible date chosen, eg. user could still be typing
        if (this.validDate(e.target.value)) {
            sessionStorage.setItem('runsheet', JSON.stringify({ date: moment(newDates.dateFrom).format('YYYYMMDD') }));
            newDates.renderDateFrom = moment(newDates.dateFrom).format('YYYY-MM-DD');
            newDates.renderDateTo = moment(newDates.dateTo).format('YYYY-MM-DD');
        }
        this.setState(newDates);
    };

    render() {
        const { classes } = this.props;
        const { dateFrom, dateTo } = this.state;

        return (
            <Fragment>
                <Grid container>
                    <Grid item xs={12} sm={3} lg={4}>
                        <Typography variant="display4" gutterBottom className={classes.pageTitle}>
                            <span>Run Sheet</span>
                            <Hidden smUp>
                                <PrintContent
                                    pageStyle={
                                        '@media print { body { -webkit-print-color-adjust: exact; color-adjust: exact; } }'
                                    }
                                    trigger={() => (
                                        <AltButton
                                            className={classes.headerButton}
                                            style={{ padding: 12, minWidth: 'unset' }}
                                        >
                                            <PrintIcon />
                                        </AltButton>
                                    )}
                                    content={() => this.formRef}
                                />
                            </Hidden>
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} lg={4} className={classes.calendarFieldsWrapper}>
                        <InlineField>
                            <DatePickerField
                                label={'From'}
                                value={dateFrom}
                                name="dateFrom"
                                onChange={this.dateChange}
                            />

                            <DatePickerField label={'Until'} value={dateTo} name="dateTo" onChange={this.dateChange} />
                        </InlineField>
                    </Grid>
                    <Hidden smDown>
                        <Grid item xs={12} sm={3} lg={4}>
                            <PrintContent
                                pageStyle={
                                    '@media print { body { -webkit-print-color-adjust: exact; color-adjust: exact; } }'
                                }
                                trigger={() => (
                                    <AltButton className={classes.headerButton}>
                                        <PrintIcon /> Print
                                    </AltButton>
                                )}
                                content={() => this.formRef}
                            />
                        </Grid>
                    </Hidden>
                </Grid>
                {this.renderReport()}
            </Fragment>
        );
    }

    renderReport() {
        const { renderDateFrom, renderDateTo } = this.state;

        return (
            <Fragment>
                <Query
                    notifyOnNetworkStatusChange
                    fetchPolicy={'network-only'}
                    query={DailyRunSheetFragment}
                    variables={{ from: renderDateFrom + ' 00:00:00', to: renderDateTo + ' 23:59:59' }}
                    ref={this.runsheetQueryRef}
                >
                    {({ loading, error, data, networkStatus }) => {
                        if (loading && networkStatus === 1) {
                            return (
                                <Fragment>
                                    <div>Loading data, please wait a moment...</div>
                                    <LinearProgress />
                                </Fragment>
                            );
                        }

                        if (error) {
                            return (
                                <Fragment>
                                    <div style={{ float: 'left' }}>
                                        <Spinner />
                                    </div>
                                    <div style={{ paddingLeft: '1.25rem', fontSize: '75%', opacity: 0.5 }}>
                                        {error ? error.toString() : 'loading...'}
                                    </div>
                                </Fragment>
                            );
                        }

                        const getDays = () => {
                            var arr = {};
                            var dt = renderDateFrom;
                            while (moment(dt) <= moment(renderDateTo)) {
                                arr[moment(dt).format('YYYY-MM-DD')] = [];
                                dt = moment(dt).add(1, 'd');
                            }
                            return arr;
                        };
                        let daylist = getDays();
                        daylist = this.addUniqueDaysToDays(data, daylist);
                        return this.renderDays(daylist, networkStatus);
                    }}
                </Query>
            </Fragment>
        );
    }

    addUniqueDaysToDays(data, daylist) {
        let filteredAppointments = this.FilterByDate(data, 'readStaffAllocations', 'Start');

        const funEvents = { readFunerals: [] };
        !!data.readFunerals &&
            data.readFunerals.edges.forEach(e => {
                if (['No Service No Attendance', 'Graveside'].indexOf(e.node.PlaceOfService.Type) === -1)
                    funEvents.readFunerals.push({ ...e.node.PlaceOfService, Funeral: e.node });
                if (e.node.Disposal.CrematedOrBuried !== 'Body Not Recovered')
                    funEvents.readFunerals.push({ ...e.node.Disposal, Funeral: e.node });
                if (e.node.RefreshmentsVenue.Type !== 'Not Required')
                    funEvents.readFunerals.push({ ...e.node.RefreshmentsVenue, Funeral: e.node });
                if (e.node.PlaceOfViewingRequired)
                    e.node.PlaceOfViewingItems.forEach(v => funEvents.readFunerals.push({ ...v, Funeral: e.node }));
                if (e.node.ReflectionRoom.ID && e.node.ReflectionRoomRequired === 'Required')
                    funEvents.readFunerals.push({ ...e.node.ReflectionRoom, Funeral: e.node });
            });

        let filteredFuneralEvents = this.FilterByDate(funEvents, 'readFunerals', 'Start');

        //cycle through appointments and create unique days
        return this.getUniqueDates(
            daylist,
            filteredAppointments.sort((a, b) => moment(a.Start) - moment(b.Start)),
            filteredFuneralEvents.sort((a, b) => moment(a.Start) - moment(b.Start)),
            'Date'
        );
    }

    getUniqueDates(days, appointments, events, key) {
        let result = [];

        appointments.forEach(function(item) {
            if (!(item[key] in days)) {
                days[item[key]] = [];
                result.push(days[item[key]]);
            }
            let tempDays = days[item[key]];
            if (!tempDays.Appointments) {
                tempDays.Appointments = [];
                tempDays.Appointments.push(item);
            } else {
                tempDays.Appointments.push(item);
            }
        });

        events.forEach(function(item) {
            if (isRelatedObjectUndefined(item.Funeral)) return;
            if (!(item[key] in days)) {
                days[item[key]] = [];
                result.push(days[item[key]]);
            }
            let tempDays = days[item[key]];
            if (!tempDays.FuneralEvents) {
                tempDays.FuneralEvents = [];
                tempDays.FuneralEvents.push(item);
            } else {
                tempDays.FuneralEvents.push(item);
            }
        });

        return days;
    }

    getGroupedBy(data, key) {
        let groups = {},
            result = [];

        data.forEach(function(item) {
            if (!(item[key] in groups)) {
                groups[item[key]] = [];
                result.push(groups[item[key]]);
            }
            groups[item[key]].push(item);
        });

        return result;
    }

    getGroupedByObj(data, key) {
        let groups = {},
            result = [];

        data.forEach(function(item) {
            if (!(item[key] in groups)) {
                groups[item[key]] = [];
                result.push(groups[item[key]]);
            }
            groups[item[key]].push(item);
        });

        return groups;
    }

    FilterByDate(data, node, dateObj) {
        const { renderDateFrom, renderDateTo } = this.state;
        const original = cloneDeep(data);
        flattenConnection(original, [node]);

        let datesTimesAdded = [];

        !!original[node] &&
            original[node].forEach(function(item, i) {
                let date = moment(item[dateObj]);

                item['Date'] = date.format('YYYY-MM-DD');

                item['Time'] = date.format('HH:mm:ss');

                if (
                    moment(renderDateFrom).isSameOrBefore(date, 'day') &&
                    moment(renderDateTo).isSameOrAfter(date, 'day')
                )
                    datesTimesAdded.push(item);
            });

        return datesTimesAdded;
    }

    renderDays(data, networkStatus) {
        const { classes } = this.props;
        const loading = !!networkStatus && networkStatus !== 7;

        let days = [];

        for (let item in data) {
            days.push(item);
        }

        return (
            <Paper className={classes.paper} elevation={0}>
                {loading && (
                    <Fragment>
                        <div>Loading data, please wait a moment...</div>
                        <LinearProgress />
                    </Fragment>
                )}
                <div className={classes.rootContainer}>
                    <div ref={e => (this.formRef = e)} className={classes.root}>
                        <Grid container spacing={3} className={classes.print}>
                            {days.map((item, i, a) => {
                                const hasApps = !!data[days[i]] && !!data[days[i]].Appointments;
                                const hasEvts = !!data[days[i]] && !!data[days[i]].FuneralEvents;
                                return (
                                    <Fragment key={i}>
                                        <Grid item xs={12} className={classes.header}>
                                            <div className={i === 0 ? '' : 'page-break'} />
                                            <Typography variant="headline">
                                                Daily Run Sheet for {moment(item).format('dddd LL')}
                                            </Typography>
                                        </Grid>

                                        {/* display appointments if they exist */}
                                        {hasApps && (
                                            <Grid item xs={12}>
                                                <Table className="table">
                                                    <TableHeaderRow>
                                                        <TableHeaderCell colSpan={3}>
                                                            {'APPOINTMENTS: ' + moment(item).format('D/MM/YYYY')}
                                                        </TableHeaderCell>
                                                    </TableHeaderRow>
                                                    {this.renderAppointments(data[days[i]])}
                                                </Table>
                                            </Grid>
                                        )}

                                        {/* display funeral events if they exist */}
                                        {hasEvts && this.renderFuneralEvents(data[days[i]])}

                                        {!loading && (!hasApps || !hasEvts) && (
                                            <Grid item xs={12}>
                                                {!hasApps && (
                                                    <Typography align={'center'}>
                                                        There are no appointments on this day.
                                                    </Typography>
                                                )}

                                                {!hasEvts && (
                                                    <Typography align={'center'}>
                                                        There are no events on this day.
                                                    </Typography>
                                                )}
                                            </Grid>
                                        )}
                                        {1 + i !== a.length && (
                                            <div
                                                className={'print-hide'}
                                                style={{
                                                    margin: 32,
                                                    height: 1,
                                                    background: '#e6e6e6'
                                                }}
                                            />
                                        )}
                                    </Fragment>
                                );
                            })}
                        </Grid>
                    </div>
                </div>
            </Paper>
        );
    }

    renderAppointments(item) {
        const { classes } = this.props;
        const uniqueAppointments = [];

        if (!item.Appointments) return null;

        const TIME = 'Time';
        const STAFF_OFFICE = 'Staff / Office';
        const REASON_LOCATION = 'Reason / Location';

        return (
            <Fragment>
                <TableSecondaryHeaderRow>
                    <TableHeaderCell className={classes.sideColumn}>
                        <span>{TIME}</span>
                    </TableHeaderCell>

                    <TableHeaderCell className={classes.extraColumn}>
                        <span>{STAFF_OFFICE}</span>
                    </TableHeaderCell>

                    <TableHeaderCell>
                        <span>{REASON_LOCATION}</span>
                    </TableHeaderCell>
                </TableSecondaryHeaderRow>
                {item.Appointments.map((item, j) => {
                    // skip duplicate appointments
                    if (!(uniqueAppointments.indexOf(item.Appointment.ID) < 0)) return null;
                    uniqueAppointments.push(item.Appointment.ID);

                    const officeName =
                        getOffices().find(e => e.LegacyKey === item.Appointment.Office) ||
                        (item.Appointment.Office === '<All Offices>' ? { label: 'All Offices' } : null);

                    const locationString = item.LocationFlattened;
                    const location = !!locationString && locationString.replace(/Phone\scall:\s/, '');
                    const phone =
                        !stringIsNullOrEmpty(item.Appointment.Phone) &&
                        item.Appointment.Phone !== location &&
                        item.Appointment.Phone;

                    return (
                        <TableRow key={j} pad className={'row page-break-avoid'}>
                            <TableCell className={`td ${classes.sideColumn}`} dataLabel={TIME}>
                                <span>
                                    {moment(item.Start).format('h:mma')} - {moment(item.End).format('h:mma')}
                                </span>
                            </TableCell>
                            <TableCell className={`td ${classes.extraColumn}`} dataLabel={STAFF_OFFICE}>
                                {item.Appointment.Members.length > 0 ? (
                                    item.Appointment.Members.sort((a, b) => (a.FirstName < b.FirstName ? -1 : 1)).map(
                                        (member, j, a) => (
                                            <span key={j}>
                                                <span>
                                                    {member.FirstName} {member.Surname}
                                                </span>
                                                {a.length !== 1 + j && ', '}
                                            </span>
                                        )
                                    )
                                ) : (
                                    <div className={classes.notSpecified}>No Allocations.</div>
                                )}
                                {!!officeName && (
                                    <div>
                                        <small>{officeName.Title}</small>
                                    </div>
                                )}
                            </TableCell>
                            <TableCell className="td" dataLabel={REASON_LOCATION}>
                                {!stringIsNullOrEmpty(item.Appointment.Reason) ? (
                                    <div>{item.Appointment.Reason}</div>
                                ) : (
                                    <div className={classes.notSpecified}>Not specified</div>
                                )}
                                {(!!location || !!phone) && (
                                    <div>
                                        <small>
                                            {!!location ? location : ''}
                                            {!!location && !!phone ? ' - ' : ''}
                                            {!!phone ? <Fragment>Call {prettyPhone(phone, false)}</Fragment> : ''}
                                        </small>
                                    </div>
                                )}
                                {!stringIsNullOrEmpty(item.Appointment.Comment) && (
                                    <div className={classes.notes}>{item.Appointment.Comment}</div>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </Fragment>
        );
    }

    renderFuneralEvents(list) {
        if (isNullOrUndefined(list.FuneralEvents)) return null;
        const { classes } = this.props;

        const funeralEvents = [];
        const funServices = list.FuneralEvents.filter(item => {
            // need to find all the services, include committals for graveside services
            return (
                item.__typename === 'FuneralPlaceOfService' ||
                (item.__typename === 'FuneralDisposal' &&
                    !!item.Funeral.PlaceOfService &&
                    item.Funeral.PlaceOfService.Type === 'Graveside')
            );
        });

        const pushViewing = [];
        list.FuneralEvents.forEach((item, i, a) => {
            let keepFun = true;

            if (!!funServices.find(fun => item.ID === fun.ID)) {
                // need to inject certain viewings immediately before their service
                const funViews = pushViewing.filter(e => e.ID === item.Funeral.ID);
                if (!!funViews && funViews.length > 0) {
                    funViews.forEach(viewing => {
                        funeralEvents.push(viewing.view);
                    });
                }
            } else {
                const funService = funServices.find(e => e.Funeral.ID === item.Funeral.ID);

                if (funService && item.__typename === 'RefreshmentsVenue') {
                    if (moment(item.Start).diff(funService.Start, 'days') === 0) {
                        // If date of service = date of refreshment, discard refreshment
                        keepFun = false;
                    }
                } else if (
                    funService &&
                    item.__typename === 'FuneralDisposal' &&
                    'Cremated' !== item.CrematedOrBuried
                ) {
                    if (
                        moment(item.Start).diff(funService.Start, 'hours') < 3 &&
                        moment(item.Start).diff(funService.Start, 'hours') >= 0
                    ) {
                        // If time of committal < 3hrs after time of service then combine into 1 event on the runsheet.
                        // If time of committal ≥ 3hrs after time of service (or before or different date) show separately.
                        keepFun = false;
                    }
                } else if (
                    funService &&
                    item.__typename === 'FuneralDisposal' &&
                    'Cremated' === item.CrematedOrBuried
                ) {
                    if (
                        moment(item.Start).diff(funService.Start, 'hours') < 2 &&
                        moment(item.Start).diff(funService.Start, 'hours') >= 0
                    ) {
                        // If time of disposal < 2hrs after time of service then combine into 1 event on the runsheet.
                        // If time of disposal ≥ 2hrs after time of service (or before or different date) show separately.
                        keepFun = false;
                    }
                } else if (funService && item.__typename === 'FuneralPlaceOfViewing') {
                    // If time of viewing ≤ 1hrs before time of service then we'll move it to appear above service.
                    if (
                        moment(item.Start).diff(funService.Start, 'hours') >= -1 &&
                        moment(item.Start).diff(funService.Start, 'hours') <= 0
                    ) {
                        keepFun = false;
                        pushViewing.push({ ID: item.Funeral.ID, view: item });
                    }
                }
            }
            if (!!keepFun) funeralEvents.push(item);
        });

        return funeralEvents.map((item, i) => {
            const property = getProperty(item, 'Funeral.LegacyKey');
            const brand = getOfficeFromKey(property);
            const rowStyle = brand?.style ? 'row--' + brand.style : '';
            return (
                <Grid item xs={12} key={i}>
                    <Table className={'page-break-avoid table'}>
                        <TableHeaderRow className={rowStyle}>
                            <TableHeaderCell colSpan="2">
                                {this.renderEventTitle(item)}
                                {this.renderBrand(item)}
                            </TableHeaderCell>
                        </TableHeaderRow>
                        <TableSecondaryHeaderRow>
                            <TableHeaderCell className={classes.sideColumn}>
                                <span>Allocated Staff</span>
                            </TableHeaderCell>

                            <TableHeaderCell>Event Instructions</TableHeaderCell>
                        </TableSecondaryHeaderRow>
                        {this.renderEvents(item, rowStyle)}
                    </Table>
                </Grid>
            );
        });
    }

    renderBrand(item) {
        if (!isNullOrUndefined(item.Funeral.LegacyKey)) {
            let brand = getOfficeFromKey(item.Funeral.LegacyKey);
            return (
                <span className={this.props.classes.brandField}>
                    <LinkButton
                        title={'View Record'}
                        classes={{
                            enabled: this.props.classes.niceLink
                        }}
                        href={this.viewRecord(item, { type: this.renderEventType(item) }, false)}
                        onClick={e => this.viewRecord(item, { type: this.renderEventType(item) })}
                        text={item.Funeral.LegacyKey}
                    />{' '}
                    | {(brand && brand.label) || '?'}
                </span>
            );
        } else {
            return null;
        }
    }

    renderEvents(item, rowStyle) {
        if (
            item.ClassName === 'FuneralManagerFuneralDisposal' ||
            item.ClassName === 'FuneralManagerFuneralPlaceOfViewing' ||
            item.ClassName === 'FuneralManagerFuneralPlaceOfService' ||
            item.ClassName === 'FuneralManagerRefreshmentsVenue' ||
            item.ClassName === 'FuneralManagerReflectionRoom'
        )
            return this.renderFuneralEventDisposal(item, rowStyle);

        console.error('unknown funeral event type', item, item.ClassName);

        return null;
    }

    renderFuneralEventDisposal(item, rowStyle) {
        const { classes } = this.props;

        return (
            <TableRow pad className={`row ${classes.alignTop} ${rowStyle}`}>
                <TableCell
                    className={`td ${classes.sideColumn} ${classes.noColumn} td--no-column`}
                    dataLabel="Allocated Staff"
                >
                    {this.renderStaffList(item.StaffAllocations)}
                    <Separator />
                    {this.renderFuneralTitle(item)}
                </TableCell>
                <TableCell
                    className={`td ${classes.packedIn} ${classes.noColumn} td--no-column`}
                    dataLabel="Event Instructions"
                >
                    {renderRunSheetDetails(item, classes)}
                </TableCell>
            </TableRow>
        );
    }

    renderStaffList(staffAllocations) {
        const { classes } = this.props;

        const trimmed = staffAllocations.filter(e => !!e); //.sort((a, b) => (a.Member.FirstName < b.Member.FirstName ? -1 : 1));
        if (trimmed.length === 0)
            return (
                <p>
                    <span className={classes.notSpecified}>No Allocations</span>
                </p>
            );

        return (
            <Fragment>
                {trimmed.map((item, index) => (
                    <p key={index}>
                        {!!item.Member && isContactDefined(item.Member) && (
                            <Fragment>
                                {item.Member.FirstName} {item.Member.Surname}
                            </Fragment>
                        )}
                        <small>
                            <span className={classes.forcedLine}>
                                {item.Start !== item.End &&
                                    `${moment(item.Start).format('h:mma')}-${moment(item.End).format('h:mma')}`}
                            </span>
                            <span className={cx(classes.forcedLine, classes.notes)}>{item.Allocation}</span>
                        </small>
                    </p>
                ))}
            </Fragment>
        );
    }

    renderEventTitle(item) {
        let name = '';
        name = joinDefined(
            [item.Funeral.FirstName, item.Funeral.MiddleName, ('' + item.Funeral.Surname).toUpperCase()],
            ' '
        );
        return (
            this.renderEventType(item).toUpperCase() +
            ': ' +
            moment(item.Start).format('h:mma') +
            ` - ${name || '(no name)'}`
        );
    }

    renderEventType(item) {
        let type = '';

        if (item.ClassName === 'FuneralManagerFuneralDisposal') {
            type = item.Funeral.Disposal.CrematedOrBuried === 'Cremated' ? 'Delivery' : 'Burial';
        } else if (item.ClassName === 'FuneralManagerFuneralPlaceOfViewing') {
            type = 'Viewing';
        } else if (item.ClassName === 'FuneralManagerFuneralPlaceOfService') {
            type = 'Service';
        } else if (item.ClassName === 'FuneralManagerRefreshmentsVenue') {
            type = 'Refreshments';
        } else if (item.ClassName === 'FuneralManagerReflectionRoom') {
            type = 'Viewing';
        } else {
            type = 'Other';
        }

        return type;
    }

    renderFuneralTitle(item) {
        let arrangers = [];
        let coordinators = [];
        let conductors = [];

        if (!isNullOrUndefined(item.Funeral.Arrangers)) {
            arrangers = joinDefined(
                item.Funeral.Arrangers.filter(item => !isNullOrUndefined(item.Member))
                    .map(item => joinDefined([item.Member.FirstName, item.Member.Surname], ' ').trim())
                    .filter(o => o),
                ', '
            );
        }

        if (!isNullOrUndefined(item.Funeral.Coordinators)) {
            coordinators = joinDefined(
                item.Funeral.Coordinators.filter(item => !isNullOrUndefined(item.Member))
                    .map(item => joinDefined([item.Member.FirstName, item.Member.Surname], ' ').trim())
                    .filter(o => o),
                ', '
            );
        }

        if (!isNullOrUndefined(item.Funeral.Conductors)) {
            conductors = joinDefined(
                item.Funeral.Conductors.filter(item => !isNullOrUndefined(item.Member))
                    .map(item => joinDefined([item.Member.FirstName, item.Member.Surname], ' ').trim())
                    .filter(o => o),
                ', '
            );
        }
        const noStaff = <span className={this.props.classes.notSpecified}>None</span>;
        return (
            <>
                <p>
                    <small>
                        Conductors:
                        <br />
                    </small>
                    {conductors.length ? conductors : noStaff}.
                </p>
                <p>
                    <small>
                        Arrangers:
                        <br />
                    </small>
                    {arrangers.length ? arrangers : noStaff}.
                </p>
                <p>
                    <small>
                        Coordinators:
                        <br />
                    </small>
                    {coordinators.length > 0 ? coordinators : noStaff}.
                </p>
            </>
        );
    }

    viewRecord = (event, category, pushURL = true) => {
        const url = '/funeral/' + event.Funeral.LegacyKey + '/' + event.Funeral.ID;
        const tab = category.type === 'Refreshments' ? 'optionals' : 'funeral-details';
        if (pushURL) this.props.history.push(url + '/' + tab);
        return url + '/' + tab;
    };
}

const styles = ({ funeralHome, palette, spacing }) => ({
    paper: {
        /*padding: spacing.unit * 3,*/
        marginTop: 32
    },
    packedIn: {
        lineHeight: 1.5
    },
    forcedLine: {
        display: 'block'
    },
    pageTitle: {
        color: palette.contentForeground[funeralHome]
    },
    fullWidth: {
        marginRight: 6
    },
    fullWidthDiv: {
        margin: '6px 0'
    },
    border: {
        border: '1px solid #aa55aa'
    },
    sideColumn: {
        width: '8.7rem',
        '& > p': {
            margin: '0.5rem 0'
        },
        '& > p:first-of-type': {
            marginTop: 0
        },
        '& > p:last-of-type': {
            marginBottom: 0
        }
    },
    noColumn: {
        paddingLeft: '0.25rem!important',
        '&[data-label]:before': {
            position: 'relative!important',
            width: 'unset!important',
            textTransform: 'uppercase',
            padding: spacing(0.5)
        }
    },
    extraColumn: {
        width: '13.3rem'
    },
    header: {
        textAlign: 'center'
    },
    headerButton: {
        float: 'right'
    },
    brandField: {
        float: 'right'
    },
    calendarFieldsWrapper: {
        display: 'flex',
        justifyContent: 'center'
    },
    notSpecified: {
        fontStyle: 'italic',
        color: '#777'
    },
    niceLink: {
        '& > span': {
            color: '#ffffff'
        },
        '& > span:hover': {
            color: '#ffffffAA'
        }
    },
    notes: {
        fontStyle: 'italic',
        paddingLeft: 4,
        borderLeft: '4px solid ' + palette.basePrimary[funeralHome] + '99',
        '& a': {
            color: 'inherit'
        }
    },
    alignTop: {
        verticalAlign: 'top'
    }
});

export default withErrorBoundary(
    compose(
        withSnackbarMessage,
        withStyles(theme => ({ ...styles(theme), ...formStyles(theme) }))
    )(DailyRunSheet),
    { fallback: ErrorFallback }
);
